import React, { useEffect, useState } from 'react';

import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { TablePagination } from '@material-ui/core';

import {
  EMPLOYEE_STATUS,
  EmployeeStatus,
  loadOrgEmployeesByStatus,
} from './actions/load-org-employees-by-status';
import { loadOrgUsers } from '../action/org/load-org-users';
import { loadEmployeeChangeLogs } from '../employee-change-logs/actions/load-employee-change-logs';
import { setEmployeeChangeLogsDirty } from '../employee-change-logs/actions/set-employee-change-logs-dirty';
import {
  employeeChangeLogsForEmployeeIdSelector,
  employeeChangeLogsStateForEmployeeIdSelector,
} from '../employee-change-logs/employee-change-log-selectors';
import { loadEmployeeGroups } from '../employee-groups/actions/load-employee-groups';
import { employeeGroupsForOrgIdSelector } from '../employee-groups/employee-group-selectors';
import { loadHrisIntegrationResponse } from '../hris-integration-response/actions/load-hris-integration-response';
import { hrisIntegrationResponseForOrgIdSelector } from '../hris-integration-response/hris-integration-response-selectors';
import { loadMergeIntegration } from '../merge-integration/actions/load-merge-integration';
import { mergeIntegrationForOrgIdSelector } from '../merge-integration/merge-integration-selectors';
import { userSelector } from '../reducers/current-user-selectors';
import { employeesForOrgIdSelector } from '../reducers/employee-selectors';
import { orgSelector, orgsForAccountId } from '../reducers/org-selectors';
import { usersForOrgIdSelector } from '../reducers/user-selectors';

import { EmployeeHistoryItem } from '../employee-change-logs/components/employee-history-item';
import { UiButton } from '../ui/ui-button';

import { getHrisDisplayName } from '../merge-integration/util/merge-display-utils';
import { ICON_NAMES, MaterialDesignIcon } from '../util/material-design-icons';
import { sortByDate } from '../util/sort-functions';
import { LoadingMessage } from '../widgets/loading-message';

type InputProps = {
  employee: Employee;
};

const mapStateToProps = (state: any, { employee }: InputProps) => {
  const org = orgSelector(state, employee.orgId);

  return {
    employeeHistory: employeeChangeLogsForEmployeeIdSelector(state, employee.employeeId),
    employeeHistoryState: employeeChangeLogsStateForEmployeeIdSelector(state, employee.employeeId),
    orgsInAccount: (org ? orgsForAccountId(state, org.accountId) : []) || [],
    orgUsers: usersForOrgIdSelector(state, employee.orgId),
    employeeGroups: employeeGroupsForOrgIdSelector(state, employee.orgId),
    employees: employeesForOrgIdSelector(state, employee.orgId),
    mergeIntegration: mergeIntegrationForOrgIdSelector(state, employee.orgId),
    hrisIntegrationResponse: hrisIntegrationResponseForOrgIdSelector(state, employee.orgId),
    user: userSelector(state),
  };
};

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { employee }: InputProps,
) => ({
  actions: {
    loadEmployeeHistory: () => dispatch(loadEmployeeChangeLogs(employee.employeeId)),
    loadUsers: () => dispatch(loadOrgUsers(employee.orgId)),
    loadEmployeeGroups: () => dispatch(loadEmployeeGroups(employee.orgId)),
    loadEmployees: (employeeStatus: EmployeeStatus) =>
      dispatch(loadOrgEmployeesByStatus(employee.orgId, employeeStatus)),
    setEmployeeHistoryDirty: () => dispatch(setEmployeeChangeLogsDirty(employee.employeeId)),
    loadMergeIntegration: () => dispatch(loadMergeIntegration(employee.orgId)),
    loadHrisIntegrationResponse: () => dispatch(loadHrisIntegrationResponse(employee.orgId)),
  },
});

type ReduxProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & ReduxProps & DispatchProps;

export const EmployeeHistory = connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  ({
    employeeHistory,
    employeeHistoryState,
    orgsInAccount,
    orgUsers,
    employeeGroups,
    employees,
    actions,
    mergeIntegration,
    hrisIntegrationResponse,
    user,
  }: Props) => {
    const [pageOffset, setPageOffset] = useState<number>(0);
    const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
    const pageSize = 10;

    useEffect(() => {
      actions.loadEmployeeHistory();
      actions.loadUsers();
      actions.loadEmployeeGroups();
      actions.loadEmployees(EMPLOYEE_STATUS.CURRENT);
      actions.loadEmployees(EMPLOYEE_STATUS.FORMER);
      if (user.isAccountAdmin) {
        actions.loadMergeIntegration();
        actions.loadHrisIntegrationResponse();
      }
    });

    // EmployeeHistory will return an empty array both when state is unloaded or when there are no matching results, so we need to
    // differentiate if it is empty because we haven't loaded yet or if it's empty because there are no results
    if (
      (!employeeHistory.length && employeeHistoryState?.loading && !isRefreshing) ||
      !orgUsers ||
      !employees ||
      !employeeGroups
    ) {
      return <LoadingMessage />;
    }

    const changePage = (event: any, page: number) => {
      setPageOffset(page * pageSize);
    };

    const refreshHistory = async () => {
      setIsRefreshing(true);
      await actions.setEmployeeHistoryDirty();
      await actions.loadEmployeeHistory();
      setIsRefreshing(false);
    };

    const orderedEmployeeHistory = employeeHistory.sort(
      sortByDate((changeLog) => changeLog.updatedAt),
    );
    const paginatedHistory = orderedEmployeeHistory.slice(pageOffset, pageOffset + pageSize);

    return (
      <>
        <div className="u-display-flex u-justifyContent-flexEnd u-padding-bottom-md u-border-bottom">
          <UiButton
            size="sm"
            variant="secondary"
            onClick={refreshHistory}
            startIcon={
              isRefreshing ? (
                ''
              ) : (
                <MaterialDesignIcon
                  iconName={ICON_NAMES.REFRESH}
                  size="sm"
                  style={{ marginBottom: 2 }}
                />
              )
            }
            loading={isRefreshing}
          >
            Refresh
          </UiButton>
        </div>
        {orderedEmployeeHistory.length ? (
          <div>
            {paginatedHistory.map((historyItem) => (
              <div
                key={`employee-history-item-${historyItem.employeeChangeLogId}`}
                className="u-border-bottom"
              >
                <EmployeeHistoryItem
                  key={historyItem.employeeChangeLogId}
                  historyItem={historyItem}
                  orgsInAccount={orgsInAccount}
                  orgUsers={orgUsers}
                  employeeGroups={employeeGroups}
                  employees={employees}
                  integrationName={getHrisDisplayName(mergeIntegration, hrisIntegrationResponse)}
                />
              </div>
            ))}
            <TablePagination
              hidden={orderedEmployeeHistory.length < pageSize}
              component="div"
              count={orderedEmployeeHistory.length}
              rowsPerPage={pageSize}
              page={pageOffset / pageSize}
              backIconButtonProps={{ 'aria-label': 'Previous Page' }}
              nextIconButtonProps={{ 'aria-label': 'Next Page' }}
              onPageChange={changePage}
              rowsPerPageOptions={[pageSize]}
            />
          </div>
        ) : (
          <div className="u-margin-top-md u-margin-left-md u-display-flex">
            <MaterialDesignIcon iconName={ICON_NAMES.REMOVE} color="gray" size="sm" />
            <div className="typo-body">No change history available</div>
          </div>
        )}
      </>
    );
  },
);
